import { afterNextRender, Component, computed, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { Faqs } from '../signup.types';

@Component({
  selector: 'app-faqs',
  imports: [
    NgbAccordionModule
  ],
  templateUrl: './faqs.component.html',
  styleUrl: './faqs.component.scss'
})
export class FaqsComponent {

  //#region Services

  private sanitizer = inject(DomSanitizer);

  //#endregion

  //#region I/O

  step = input.required<number>();

  //#endregion

  //#region Properties

  faqs = signal<Faqs[]>([]);

  faqsInView = computed(() => this.faqs().find(x => x.step === this.step())?.data ?? []);

  //#endregion

  //#region Constructors

  constructor() {
    afterNextRender(() => {
      this.faqs.set([
        {
          step: 0,
          data: [
            {
              question: `Do I have to have a subscription? How does ordering work?`,
              answer: this.#sanitizeHtml(`
            <p>
              No, a subscription is not required. We designed our system to be flexible so you can order in the way that works best for you. During sign-up, you can choose to add produce boxes or subscribe and save items, but you do not have to. You can also create an account with no subscriptions and place orders week by week.
            <p>
            `)
            },
            {
              question: `How do I know the food is local?`,
              answer: this.#sanitizeHtml(`
            <p>
              We define local as anything grown or made within 150 miles of Philadelphia. Each product lists the farmer or food artisan who produced it, so you always know where it came from. About 90 percent of everything we sell is local.
            </p>
            <p>
              We do carry a few non-local staples to round out your cooking needs and to supplement what our farmers can provide during the winter. Even when items are not local, we do our best to source them directly from sustainable farmers.
            </p>
            `)
            },
            {
              question: `Is your food organic or chemical-free?`,
              answer: this.#sanitizeHtml(`
            <p>
              We prioritize chemical-free, regenerative agriculture as much as possible, and much of our produce is certified organic and certified IPM (Integrated Pest Management). Many of our smaller partner farms cannot afford certification, but they use organic methods all the same. For these farmers, sustainability is personal, and they avoid anything that could harm their families working alongside them in the fields.
            </p>
            `)
            },
          ]
        },
        {
          step: 1,
          data: [
            {
              question: `Why do you only deliver on certain days?`,
              answer: this.#sanitizeHtml(`
             <p>
              Since we are a small business, we design our delivery system to be as sustainable as possible. By grouping deliveries by neighborhood, we cut down on the miles we drive and keep our process efficient. On average, we drive just 1 mile per delivery, while a typical trip to the grocery store adds up to 7 miles.
             </p>
            `)
            },
            {
              question: `What if I am not home during the delivery?
`,
              answer: this.#sanitizeHtml(`
            <p>
              If you live somewhere that does not allow packages to be left, please email us at <a href="mailto: info@phillyfoodworks.com">info@phillyfoodworks.com</a> and we will work with you to find a solution.
            </p>
            <p>
              If packages can be left at your home but you will not be there, you can leave out a cooler and our driver will place your order inside. Just add a note for the driver so they know. We have a small, dedicated team of drivers, and in most cases the same person will deliver to you each week.
            </p>
            `)
            },
            {
              question: `What do I do if the availible delivery day or timslot does not work for me?`,
              answer: this.#sanitizeHtml(`
            <p>
              We want everyone to be able to support local producers, and we will do our best to make it work. The easiest approach is to finish setting up your account and then email us at info@phillyfoodworks.com with the days and times you are available. Our distribution manager, Chris, will work with you to find a delivery time that fits.
            </p>
            `)
            },
          ]
        },
        {
          step: 2,
          data: [
            {
              question: `What is the difference between a customizable and a non-customizable share?`,
              answer: this.#sanitizeHtml(`
              <p>
                If you choose a customizable share, we fill your box based on your selected size and what is fresh that week. Starting Friday at 7am, you can remove anything you do not want and also add other items from our marketplace. You are only charged for the items you keep, and your order must be at least $28 to ship.
              </p>
              <p>
                If you choose a non-customizable share, your box comes as is, but you can always skip that week and order directly from the marketplace instead. There is never a fee to skip, pause, or cancel your share as long as you do it before 7 am the day before delivery.
              </p>
            `)
            },
            {
              question: `What are the benefits to getting a farm share? `,
              answer: this.#sanitizeHtml(`
              <p>
                Farm shares are one of the best ways to support local farmers while discovering fresh, seasonal foods. Each week, our produce buyer, Anne, works with farmers to select what is ready for harvest. She creates boxes with a balanced mix of fruits and vegetables that are great for both cooking and eating fresh.
              </p>
              <p>
                Farm shares also give farmers a dependable outlet for their produce, which helps them crop plan and grow with confidence. Customers benefit by trying new foods they might not normally order and by eating more seasonally. For those who like flexibility, customizable farm shares allow you to choose what goes in your box. For those who enjoy variety and want to save a little money, non-customizable farm shares provide whatever is fresh from the farmers’ harvest.
              </p>`)
            },
            {
              question: `How does the weekly ordering schedule work for edits to my share or skipping my share altogether?`,
              answer: this.#sanitizeHtml(`
              <p>
                At the start of each week, our produce buyer, Anne, talks with farmers to find out what they will have available. After some back and forth, she builds the best shares possible and loads the items into your account on Friday morning.
              </p>
              <p>
                From there, you can edit your share or add items from our online market anytime between 7 am Friday and 7 am the day before your delivery. For example, if your delivery is on Wednesday, you can make changes between 7 am Friday and 7 am Tuesday. After that, our team begins sourcing, packing, and preparing your food for delivery.
              </p>
              `)
            },
          ]
        },
        {
          step: 3,
          data: [
            {
              question: `How does the subscribe and save discount apply to my order?`,
              answer: this.#sanitizeHtml(`
              <p>
                When you add a subscription product to your cart, it will automatically appear at a 5% discount. You will only be billed the discounted amount.
              </p>
              `)
            },
            {
              question: `Are there any fees with skipping or cancelling my subscribe and save items?`,
              answer: this.#sanitizeHtml(`
              <p>
                No. If you skip, pause, or cancel your subscribe and save items before 7 am the day before your delivery, there is no fee.
              </p>
              <p>
                If you cancel after 7 am the day before your delivery, we may charge a small cancellation fee of $5 to $10 depending on the product, since we have already purchased and started packing your food.
              </p>
              `)
            },
            {
              question: `How does the weekly ordering schedule work for changes to my subscribe and save items?`,
              answer: this.#sanitizeHtml(`
              <p>
                You can manage your subscribe and save items from the “subscriptions” page in your account. There, you can update the delivery date, frequency, and quantity of each item.
              </p>
              <p>
                If you want to remove an item from your current order, you must do so before 7 am the day before delivery. For example, if your delivery is on Wednesday, you need to make changes before 7 am on Tuesday. After that, our team begins sourcing, packing, and preparing your food for delivery.
              </p>
              `)
            }
          ],
        },
        {
          step: 4,
          data: [
            {
              question: `Are there any fees or costs associated with signing up?`,
              answer: this.#sanitizeHtml(`
              <p>
                Nope! Creating an account is completely free. If you sign up for a farm share or a subscribe and save subscription, you will not be billed until the day of delivery. You can skip a delivery or cancel at any time without being charged. We want everything to be simple and transparent.
              </p>
              `)
            },
            {
              question: `Why do you only deliver on certain days?`,
              answer: this.#sanitizeHtml(`
              <p>
                Since we are a small business, we design our delivery system to be as sustainable as possible. By grouping deliveries by neighborhood, we cut down on the miles we drive and keep our process efficient. On average, we drive just 1 mile per delivery, while a typical trip to the grocery store adds up to 7 miles.
              </p>
              `)
            },
            {
              question: `What kinds of communications will you send me?`,
              answer: this.#sanitizeHtml(`
              <p>
                Each week, we send an email with updates on what is new and in season. Since we work with more than 200 local producers, availability changes often, so there is always something fresh to share.
              </p>
              <p>
                We also send information about the food and the people who make it. This can include recipes, storage tips, producer stories, and updates on how we are building a stronger local food system together. You will also receive tips and tricks for using your Philly Foodworks account, including ways to save money while supporting local producers.
              </p>
              `)
            }
          ]
        },
        {
          step: 5,
          data: [
            {
              question: `When will you charge my credit card?`,
              answer: this.#sanitizeHtml(`
              <p>
                Your credit card is charged on the day of delivery. You can make changes or cancel your order until 7 am the day before delivery.
              </p>
              `)
            },
            {
              question: `Can I cancel my Philly Foodworks account any time?`,
              answer: this.#sanitizeHtml(`
              <p>
                Absolutely. Just send us an email at info@phillyfoodworks.com and we will close your account for you. If you are only looking to take a break, you can leave your account open for as long as you want. You will not be billed unless you place an order.
              </p>
              `)
            },
            {
              question: `Why should I choose to order my food with Philly Foodworks and support your business?`,
              answer: this.#sanitizeHtml(`
              <p>
                We started this business more than a decade ago because, like you, we want a strong local food system. We live here. We care about creating a sustainable ecosystem where both people and the earth are treated fairly. We also want to enjoy the best food possible with our friends and families, and we work hard every day to make that possible.
              </p>
              <p>
                When you shop with us, you gain access to the largest selection of local food in the tri-state area through a convenient market supported by a dependable community business. You can feel confident knowing you are supporting local producers and enjoying food you will love—or your money back.
              </p>
              `)
            }
          ]
        }
      ]);
    });
  }

  //#endregion

  //#region Methods

  #sanitizeHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  //#endregion
}
